import { useRouter } from 'next/router';
import { useMemo, useState } from 'react';
import {
    AppliedFilters,
    ContextFilters,
    Filters,
    MobileFilters,
    NavigationDesktop,
    NavigationMobile,
    ProductList,
} from '~/features/ProductList';
import { getTranslatedSortingName } from '~/features/ProductList/components/helpers/filterHelper';
import useFilter from '~/features/ProductList/hooks/useFilter';
import PlusIcon from '~/icons/iconography/plus.svg';
import { UmbracoTypes } from '~/lib/data-contract';
import { SortOptions } from '~/services/loop54/types';
import { useFilterDefaults, usePage, useTranslations } from '~/services/umbraco/hooks';
import { Gutter } from '~/shared/components/Gutter';
import { MaxWidth } from '~/shared/components/MaxWidth';
import { Modal } from '~/shared/components/Modal';
import { Pagination } from '~/shared/components/Pagination';
import { ScrollToTop } from '~/shared/components/ScrollToTop';
import { Sorting } from '~/shared/components/Sorting';
import gtm from '~/shared/tracking/gtm';
import DynamicBlocks from '~/templates/blocks';
import {
    Content,
    FilterAndSortingWrapper,
    FiltersButtonMobile,
    FiltersContainer,
    Navigation,
    PageTitle,
    Wrapper,
} from './styled';
import { Schema, SchemaTypes } from '~/shared/schema';
import { NavigationFilter } from '~/features/ProductList/components/NavigationFilter';
import { PageHeroElement } from '~/templates/blocks/components/M75TinyMediaText/M75TinyMediaText';
import { useMedia } from 'react-use';
import { queries } from '~/theme';
import CategorySeoFaq from './components/CategorySeoFaq';

export default function P40ProductListPage() {
    const { translate } = useTranslations();
    const { isReady } = useRouter();
    const [showMobileFilters, setShowMobileFilters] = useState(false);
    const { data: defaultSortOptions } = useFilterDefaults((data) => data.defaultSortOptions);
    const { data: page } = usePage<UmbracoTypes.IP40ProductListPage>();
    const { data: filter, isLoading } = useFilter(true);
    const [hasLeftNavigation, setHasLeftNavigation] = useState(true);
    const isDesktop = useMedia(queries.sm, false);

    const pageFilterConfig = page?.pageProperties?.filterAdmin;
    const defaultSort = pageFilterConfig?.sort;
    const sortOptions = pageFilterConfig?.sortOptions?.length
        ? (Object.fromEntries(
              Object.entries(defaultSortOptions ?? {}).filter(([key]) =>
                  pageFilterConfig.sortOptions.includes(key),
              ),
          ) as SortOptions)
        : defaultSortOptions;
    const contextList = (filter?.customData?.contextGroups as unknown as { key: string }[])?.map(
        (context) => context.key,
    );
    const pageHasResults = useMemo(
        () => isReady && !isLoading && filter?.results?.count,
        [filter?.results?.count, isLoading, isReady],
    );
    const pageTitle = `${page?.title}${pageHasResults ? ` (${filter?.results?.count})` : ''}`;

    const items = useMemo(() => filter?.results?.items, [filter?.results?.items]);

    const verticalFilters = useMemo(
        () => filter?.results?.facets?.filter((facet) => facet.name === 'CategoryPath'),
        [filter?.results?.facets],
    );

    const horizontalFilters = useMemo(
        () => filter?.results?.facets?.filter((facet) => facet.name !== 'CategoryPath'),
        [filter?.results?.facets],
    );
    const { asPath } = useRouter();

    const pageHero = useMemo(
        () => PageHeroElement(page?.pageHeroElement, asPath),
        [asPath, page?.pageHeroElement],
    );

    return (
        <>
            {!!pageHasResults ? <Schema type={SchemaTypes.ItemList} props={{ items }} /> : null}

            <MaxWidth>
                <Gutter>
                    <Wrapper>
                        <Navigation>
                            <NavigationDesktop
                                hasLeftNavigation={hasLeftNavigation}
                                setHasLeftNavigation={setHasLeftNavigation}
                            />

                            {!hasLeftNavigation && isDesktop ? (
                                <NavigationFilter
                                    searchNavigationItems={verticalFilters}
                                    filterType="desktop"
                                />
                            ) : null}
                        </Navigation>

                        <Content>
                            <DynamicBlocks
                                elements={pageHero as UmbracoTypes.IModules[]}
                                hasGutter={false}
                            />

                            <PageTitle variant="display4" as="h1">
                                {pageTitle}
                            </PageTitle>

                            {!!pageHasResults ? (
                                <>
                                    {!hasLeftNavigation ? (
                                        <NavigationFilter
                                            searchNavigationItems={verticalFilters}
                                            filterType="mobile"
                                        />
                                    ) : null}

                                    <NavigationMobile />
                                </>
                            ) : null}

                            <ContextFilters contextList={contextList} />

                            {!!pageHasResults ? (
                                <>
                                    {' '}
                                    <Filters facets={horizontalFilters} />
                                    <FiltersContainer>
                                        <FilterAndSortingWrapper>
                                            <FiltersButtonMobile
                                                customIcon={<PlusIcon />}
                                                onClick={() => {
                                                    setShowMobileFilters(true);
                                                }}
                                            >
                                                {translate('navigation.filters')}
                                            </FiltersButtonMobile>

                                            {sortOptions && (
                                                <Sorting
                                                    items={sortOptions}
                                                    defaultSort={defaultSort}
                                                    onSelectedItemsChange={(selectedItemKey) =>
                                                        gtm.custom.sorting(
                                                            getTranslatedSortingName(
                                                                selectedItemKey,
                                                                translate,
                                                            ) ?? selectedItemKey,
                                                        )
                                                    }
                                                />
                                            )}
                                        </FilterAndSortingWrapper>

                                        <AppliedFilters />
                                    </FiltersContainer>
                                    {isLoading ? (
                                        <ProductList.Skeleton />
                                    ) : (
                                        <ProductList items={items} itemListName={pageTitle} />
                                    )}
                                </>
                            ) : null}

                            <Pagination
                                total={filter?.results?.count}
                                taken={filter?.results?.items?.length}
                                isLoading={isLoading}
                            />

                            <DynamicBlocks
                                elements={page?.pageElements as UmbracoTypes.IModules[]}
                            />

                            <CategorySeoFaq
                                channelNodeSeoFaq={page?.pageProperties?.channelNodeSeoFaq}
                            />
                        </Content>
                    </Wrapper>
                </Gutter>

                {!!pageHasResults ? <ScrollToTop /> : null}
            </MaxWidth>

            {!!pageHasResults ? (
                <>
                    {' '}
                    <Modal
                        handleClose={() => {
                            setShowMobileFilters(false);
                        }}
                        isOpen={showMobileFilters}
                        noHeader
                    >
                        <MobileFilters
                            facets={filter?.results?.facets}
                            onClose={() => {
                                setShowMobileFilters(false);
                            }}
                            resultCount={filter?.results?.count}
                        />
                    </Modal>
                </>
            ) : null}
        </>
    );
}
